import EventEntryPage from "@features/access/pages/EventEntryPage";
import { useAuthStore } from "@features/authentication/stores/authStore";
import { DiscoveryPage } from "@features/discovery";
import LikedYouPage from "@features/discovery/pages/LikedYouPage";
import { LandingPage } from "@features/landing";
import { LegalPage } from "@features/legal";
import { ChatPage } from "@features/messaging/pages/ChatPage";
import { MatchesPage } from "@features/messaging/pages/MatchesPage";
import { MatchProfilePage } from "@features/messaging/pages/MatchProfilePage";
import { OnboardingPage } from "@features/onboarding";
import { PaymentFailedPage, PaymentSuccessPage, PayPalSandboxHarnessPage } from "@features/payment";
import { ProfilePage } from "@features/profile";
import ProfilePreviewPage from "@features/profile/pages/ProfilePreviewPage";
import AnimationPlayground from "@shared/components/AnimationPlayground";
import { HeadlessUIShowcase } from "@shared/components/UIShowcase";
import { lazy } from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { AdminRoute, AuthRoute, ProtectedRoute, PublicRoute } from "./guards";
import { AuthLayout, MainLayout, NotFound, OnboardingLayout } from "./layouts";

const loadAdminPages = () => import("@features/admin/admin");
const loadAuthPages = () => import("@features/authentication/auth");

const AdminAccountDeletionRequestsPage = lazy(() =>
  loadAdminPages().then((m) => ({ default: m.AdminAccountDeletionRequestsPage }))
);
const AdminAnalyticsPage = lazy(() => loadAdminPages().then((m) => ({ default: m.AdminAnalyticsPage })));
const AdminEventDetailPage = lazy(() => loadAdminPages().then((m) => ({ default: m.AdminEventDetailPage })));
const AdminEventsPage = lazy(() => loadAdminPages().then((m) => ({ default: m.AdminEventsPage })));
const AdminOrphanedUsersPage = lazy(() => loadAdminPages().then((m) => ({ default: m.AdminOrphanedUsersPage })));
const AdminSafetyAlertsPage = lazy(() => loadAdminPages().then((m) => ({ default: m.AdminSafetyAlertsPage })));
const AdminSafetyAlertDetailPage = lazy(() =>
  loadAdminPages().then((m) => ({ default: m.AdminSafetyAlertDetailPage }))
);
const PhoneAuthPage = lazy(() => loadAuthPages().then((m) => ({ default: m.PhoneAuthPage })));

// TODO: have these work without the page wrappers

function DiscoveryPageWrapper() {
  const { userId } = useAuthStore();
  const location = useLocation();
  const startWithUserId = (location.state as { startWithUserId?: string } | null)?.startWithUserId;

  if (!userId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">Please sign in to discover people.</p>
        </div>
      </div>
    );
  }

  return <DiscoveryPage currentUserId={userId} {...(startWithUserId ? { startWithUserId } : {})} />;
}

function LikedYouPageWrapper() {
  const { userId } = useAuthStore();

  if (!userId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">Please sign in to see who liked you.</p>
        </div>
      </div>
    );
  }

  return <LikedYouPage />;
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  {
    element: <PublicRoute />,
    children: [
      { index: true, element: <LandingPage /> },
      { path: "showcase", element: <HeadlessUIShowcase /> },
      { path: "animation-playground", element: <AnimationPlayground /> },
      { path: "legal", element: <LegalPage /> },
      { path: "payment-failed", element: <PaymentFailedPage /> },
      { path: "payment-success", element: <PaymentSuccessPage /> },
      { path: "app/paypal-sandbox", element: <PayPalSandboxHarnessPage /> },
      { path: "paypal-sandbox", element: <Navigate replace to="/app/paypal-sandbox" /> },
      { path: "privacy", element: <LegalPage /> },
      { path: "terms", element: <LegalPage /> },
      {
        path: "admin",
        element: <AdminRoute />,
        children: [
          { index: true, element: <AdminEventsPage /> },
          { path: "analytics", element: <AdminAnalyticsPage /> },
          { path: "alerts", element: <AdminSafetyAlertsPage /> },
          { path: "alerts/:alertId", element: <AdminSafetyAlertDetailPage /> },
          { path: "orphaned-users", element: <AdminOrphanedUsersPage /> },
          { path: "deletion-requests", element: <AdminAccountDeletionRequestsPage /> },
          { path: "events/:eventId", element: <AdminEventDetailPage /> },
        ],
      },
      { path: ":eventId", element: <EventEntryPage /> },
    ],
  },
  {
    // these routes are only for unauthenticated users
    element: <AuthRoute />,
    children: [
      {
        // the layout wrapper
        element: <AuthLayout />,
        children: [
          {
            path: "auth",
            element: <PhoneAuthPage />,
          },
        ],
      },
    ],
  },
  {
    // all routes here require auth (handles onboarding redirection automatically)
    element: <ProtectedRoute />,
    children: [
      {
        // the layout wrapper
        element: <MainLayout />,
        children: [
          {
            path: "/discover",
            element: <DiscoveryPageWrapper />,
          },
          {
            path: "/liked-you",
            element: <LikedYouPageWrapper />,
          },
          {
            path: "/matches",
            element: <MatchesPage />,
          },
          {
            path: "/profile",
            element: <ProfilePage />,
          },
          {
            path: "/access-expired",
            element: <Navigate to="/discover" replace />,
          },
          {
            path: "/profile/preview",
            element: <ProfilePreviewPage />,
          },
          {
            path: "/match/:userId",
            element: <MatchProfilePage />,
          },
          {
            path: "/chat/:chatId",
            element: <ChatPage />,
          },
        ],
      },
      {
        // onboarding layout wrapper
        element: <OnboardingLayout />,
        children: [{ path: "/onboarding", element: <OnboardingPage /> }],
      },
    ],
  },
  { path: "*", element: <NotFound /> },
]);
